import { useEffect, useState } from "react";
import { Alert, Button, MenuItem, Stack, TextField } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { isApiError } from "@bw/shared/api";
import type {
  CommercialFeePolicyDraft,
  CommercialFeePublicationReview,
  CommercialPublicationRequest,
} from "@bw/shared/types/generated";
import { PlatformPage } from "@/components/PlatformPage";
import { FeePolicyPublicationFacts } from "../components/FeePolicyPublicationFacts";
import { useFeePolicyDraft, useFeePolicyPublicationReview, usePublishFeePolicy } from "../hooks/useFeePolicies";

type Environment = CommercialPublicationRequest["environment"];
type Attempt = {
  review: CommercialFeePublicationReview;
  draft: CommercialFeePolicyDraft;
  request: CommercialPublicationRequest;
};

function sameCandidate(review: CommercialFeePublicationReview, draft: CommercialFeePolicyDraft,
  draftId: string, environment: Environment): boolean {
  return review.candidate.kind === "fee_policy" && review.candidate.draft_id === draftId &&
    review.candidate.environment === environment && draft.draft_id === draftId &&
    review.candidate.draft_revision === draft.revision &&
    review.candidate.audience_org_id === draft.audience_org_id;
}

function PublicationController({ draftId, environment, onAttemptChange }: {
  draftId: string; environment: Environment; onAttemptChange: (locked: boolean) => void;
}) {
  const reviewQuery = useFeePolicyPublicationReview(draftId, environment);
  const draftQuery = useFeePolicyDraft(draftId);
  const mutation = usePublishFeePolicy(draftId);
  const navigate = useNavigate();
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [captured, setCaptured] = useState<Attempt | null>(null);
  const [authorityDenied, setAuthorityDenied] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const denied = [reviewQuery.error, draftQuery.error, mutation.error].some(
    (error) => isApiError(error) && error.status === 403,
  );
  const terminal = isApiError(mutation.error) && mutation.error.status >= 400 &&
    mutation.error.status < 500 && mutation.error.status !== 408 && mutation.error.status !== 429;
  useEffect(() => { if (denied) setAuthorityDenied(true); }, [denied]);
  useEffect(() => { onAttemptChange(Boolean(captured) || mutation.isPending || authorityDenied || denied); },
    [captured, mutation.isPending, authorityDenied, denied, onAttemptChange]);

  async function reviewCurrent() {
    if (reviewing || mutation.isPending) return;
    setReviewing(true);
    try {
      const [review, draft] = await Promise.all([reviewQuery.refetch(), draftQuery.refetch()]);
      if (review.isSuccess && draft.isSuccess && sameCandidate(review.data, draft.data, draftId, environment)) {
        // A recovered read cannot resolve an unknown publication outcome.
        // Retain that original POST even when the saved draft has changed.
        if (!captured || terminal) {
          setCaptured(null);
          setKey(crypto.randomUUID());
          mutation.reset();
        }
        setAuthorityDenied(false);
      }
    } finally {
      setReviewing(false);
    }
  }

  if (denied || authorityDenied) return (
    <Alert severity="warning">
      Current Admin publication access unavailable.
      <Button size="small" disabled={reviewing} onClick={() => void reviewCurrent()}>Review current access</Button>
    </Alert>
  );
  const current = reviewQuery.data && draftQuery.data &&
    sameCandidate(reviewQuery.data, draftQuery.data, draftId, environment)
    ? { review: reviewQuery.data, draft: draftQuery.data } : null;
  const displayed = captured ?? current;
  const freshBlocked = !current || reviewQuery.isError || draftQuery.isError ||
    !current.review.source_available || current.review.environment_allowed !== true ||
    Boolean(current.review.unresolved_gates.length || current.review.blockers.length);
  const blocked = mutation.isPending || reviewing || terminal || (!captured && freshBlocked);
  return (
    <Stack spacing={2}>
      {reviewQuery.isError || draftQuery.isError ? (
        <Alert severity="warning">
          Current publication review unavailable.
          <Button size="small" disabled={reviewing || mutation.isPending} onClick={() => void reviewCurrent()}>Retry review</Button>
        </Alert>
      ) : null}
      {displayed ? (
        <FeePolicyPublicationFacts review={displayed.review} draft={displayed.draft}
          attempt={captured?.request ?? { expected_revision: displayed.review.candidate.draft_revision,
            environment, idempotency_key: key }} isPending={blocked}
          isRetry={Boolean(captured && mutation.isError && !terminal)}
          error={mutation.isError ? (terminal
            ? "Publication was denied. Reload and review before another attempt."
            : "Publication result unknown. Retry the original attempt without changing its body.") : null}
          onPublish={(request) => {
            if (blocked) return;
            const original = captured ?? { ...displayed, request };
            setCaptured(original);
            mutation.mutate(original.request, { onSuccess: (result) =>
              navigate(`/contracts/fee-policies/versions/${result.fee_policy_version_id}`) });
          }} />
      ) : reviewQuery.isPending || draftQuery.isPending ? (
        <Alert severity="info" role="status">Loading fee publication candidate…</Alert>
      ) : (
        <Alert severity="warning">
          The current candidate and saved draft could not be matched.
          <Button size="small" disabled={reviewing} onClick={() => void reviewCurrent()}>Reload and review candidate</Button>
        </Alert>
      )}
      {terminal ? (
        <Button size="small" variant="outlined" disabled={reviewing}
          onClick={() => void reviewCurrent()}>Reload and review candidate</Button>
      ) : null}
    </Stack>
  );
}

function PublicationWorkspace({ draftId }: { draftId: string }) {
  // Sandbox selects the initial review only; the server owns publication policy.
  const [environment, setEnvironment] = useState<Environment>("sandbox");
  const [locked, setLocked] = useState(false);
  return (
    <Stack spacing={2}>
      <TextField size="small" select label="Review environment" value={environment}
        disabled={locked} onChange={(event) => setEnvironment(event.target.value as Environment)}>
        <MenuItem value="sandbox">Sandbox</MenuItem>
        <MenuItem value="production">Production</MenuItem>
      </TextField>
      <PublicationController key={environment} draftId={draftId} environment={environment} onAttemptChange={setLocked} />
    </Stack>
  );
}

export function FeePolicyPublicationPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const { user } = useAuth();
  return (
    <PlatformPage title="Fee publication review" actions={
      <Button size="small" component={Link} to="/contracts/fee-policies">Back to fee drafts</Button>
    }>
      {draftId && user?.uid ? (
        <PublicationWorkspace key={`${user.uid}:${draftId}`} draftId={draftId} />
      ) : <Alert severity="warning">Current Admin draft context unavailable.</Alert>}
    </PlatformPage>
  );
}
